import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo } from "react";

import type { FileListItem } from "../../types/project-storage.types";
import { formatFileSize } from "../../utils/formatFileSize";
import { getFileExtensionIcon } from "../../utils/file-type-icon";

type Props = {
  files: FileListItem[];
  selectedFileIds: Set<string>;
  canEdit?: boolean;
  downloadingFileId: string | null;
  isDeleting?: boolean;
  onToggleFile: (fileId: string) => void;
  onToggleAll: () => void;
  onDownload: (file: FileListItem) => void;
  onRename?: (file: FileListItem) => void;
  onDelete?: (file: FileListItem) => void;
};

export function FileTable({
  files,
  selectedFileIds,
  canEdit = false,
  downloadingFileId,
  isDeleting = false,
  onToggleFile,
  onToggleAll,
  onDownload,
  onRename,
  onDelete,
}: Props) {
  const allSelected = files.length > 0 && selectedFileIds.size === files.length;
  const someSelected =
    selectedFileIds.size > 0 && selectedFileIds.size < files.length;

  const sortedFiles = useMemo(
    () => [...files].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [files]
  );

  if (files.length === 0) return null;

  return (
    <TableContainer
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 1,
        bgcolor: "#FFFFFF",
        mt: 2,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#F9FAFB" }}>
            <TableCell padding="checkbox" sx={{ width: 48 }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onToggleAll}
              />
            </TableCell>
            <TableCell sx={{ width: 80 }}>
              <HeaderLabel label="Tipo" />
            </TableCell>
            <TableCell>
              <HeaderLabel label="Nome" />
            </TableCell>
            <TableCell sx={{ width: 130 }}>
              <HeaderLabel label="Tamanho" />
            </TableCell>
            <TableCell sx={{ width: canEdit ? 132 : 84 }} align="center">
              <Typography fontSize={14} fontWeight={600} color="#374151">
                Ações
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedFiles.map((file) => {
            const Icon = getFileExtensionIcon(file.extension);
            const isDownloading = downloadingFileId === file.id;

            return (
              <TableRow
                key={file.id}
                hover
                selected={selectedFileIds.has(file.id)}
                sx={{ "&:last-child td": { borderBottom: 0 } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedFileIds.has(file.id)}
                    onChange={() => onToggleFile(file.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" justifyContent="center">
                    <Icon sx={{ fontSize: 22, color: "#1361A4" }} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontSize={14} color="#1F2937" noWrap>
                    {file.displayName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontSize={14} color="#6B7280">
                    {formatFileSize(file.sizeBytes)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.25}>
                    <Tooltip title="Download">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onDownload(file)}
                          disabled={isDownloading || isDeleting}
                          aria-label={`Download ${file.displayName}`}
                        >
                          {isDownloading ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DownloadOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {canEdit && onRename && (
                      <Tooltip title="Renomear">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onRename(file)}
                            disabled={isDeleting}
                            aria-label={`Renomear ${file.displayName}`}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canEdit && onDelete && (
                      <Tooltip title="Excluir">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onDelete(file)}
                            disabled={isDeleting}
                            aria-label={`Excluir ${file.displayName}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function HeaderLabel({ label }: { label: string }) {
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography fontSize={14} fontWeight={600} color="#374151">
        {label}
      </Typography>
      <UnfoldMoreIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
    </Box>
  );
}
